// ═══════════════════════════════════════════════════════════════════════════
// WRITTN - Journal Editor Component
// ═══════════════════════════════════════════════════════════════════════════
// Slide-up panel for writing and editing verse reflections
// ═══════════════════════════════════════════════════════════════════════════

import { icons } from './icons.js';
import {
    saveJournalEntry,
    getJournalEntriesForVerse,
    deleteJournalEntry
} from '../services/storage.js';
import { logJournalCreate, logJournalEdit } from '../services/analytics.js';

let currentOverlay = null;
let currentVerse = null;
let currentEntry = null;

export function showJournalEditor(verse, existingEntry = null) {
    if (currentOverlay) return;

    currentVerse = verse;
    currentEntry = existingEntry;

    const isEditing = !!existingEntry;
    const entries = getJournalEntriesForVerse(verse.reference);

    const overlay = document.createElement('div');
    overlay.className = 'journal-overlay';
    overlay.id = 'journal-overlay';

    overlay.innerHTML = `
    <div class="journal-panel">
      <div class="journal-handle"></div>
      
      <div class="journal-header">
        <h2 class="journal-title">${isEditing ? 'Edit Note' : 'Add Note'}</h2>
        <button class="journal-close-btn" id="journal-close" aria-label="Close">
          ${icons.x}
        </button>
      </div>
      
      <div class="journal-verse-ref">
        <span class="journal-verse-reference">${verse.reference}</span>
        <p class="journal-verse-text">${verse.text.length > 100 ? verse.text.slice(0, 100) + '...' : verse.text}</p>
      </div>
      
      ${entries.length > 0 && !isEditing ? `
        <div class="journal-existing">
          <span class="journal-existing-label">Previous notes (${entries.length})</span>
          <button class="journal-view-all-btn" id="journal-view-all">View All</button>
        </div>
      ` : ''}
      
      <div class="journal-editor-area">
        <textarea 
          class="journal-textarea" 
          id="journal-content"
          placeholder="What does this verse mean to you? How does it apply to your life?"
          rows="8"
        >${existingEntry?.content || ''}</textarea>
        
        <div class="journal-editor-footer">
          <span class="journal-word-count" id="journal-word-count">0 words</span>
          ${isEditing ? `
            <button class="journal-delete-btn" id="journal-delete">
              Delete
            </button>
          ` : ''}
        </div>
      </div>
      
      <div class="journal-actions">
        <button class="journal-save-btn" id="journal-save">
          ${isEditing ? 'Update Note' : 'Save Note'}
        </button>
      </div>
    </div>
  `;

    document.body.appendChild(overlay);
    currentOverlay = overlay;

    // Animate in
    requestAnimationFrame(() => {
        overlay.classList.add('active');
    });

    // Setup event handlers
    setupJournalHandlers(overlay);

    // Update word count initially
    updateWordCount();

    // Focus the textarea
    setTimeout(() => {
        overlay.querySelector('#journal-content')?.focus();
    }, 300);
}

function setupJournalHandlers(overlay) {
    const closeBtn = overlay.querySelector('#journal-close');
    const saveBtn = overlay.querySelector('#journal-save');
    const deleteBtn = overlay.querySelector('#journal-delete');
    const viewAllBtn = overlay.querySelector('#journal-view-all');
    const textarea = overlay.querySelector('#journal-content');

    // Close button
    closeBtn.addEventListener('click', hideJournalEditor);

    // Click outside to close
    overlay.addEventListener('click', (e) => {
        if (e.target === overlay) {
            hideJournalEditor();
        }
    });

    // ESC key to close
    const handleEsc = (e) => {
        if (e.key === 'Escape') {
            hideJournalEditor();
            document.removeEventListener('keydown', handleEsc);
        }
    };
    document.addEventListener('keydown', handleEsc);

    // Word count update
    textarea.addEventListener('input', updateWordCount);

    // Save button
    saveBtn.addEventListener('click', handleSave);

    // Delete button
    if (deleteBtn) {
        deleteBtn.addEventListener('click', handleDelete);
    }

    // View all button
    if (viewAllBtn) {
        viewAllBtn.addEventListener('click', () => {
            hideJournalEditor();
            // Import dynamically to avoid circular deps
            import('./JournalViewer.js').then(module => {
                module.showJournalViewer(currentVerse.reference);
            });
        });
    }
}

function updateWordCount() {
    const textarea = document.querySelector('#journal-content');
    const countEl = document.querySelector('#journal-word-count');
    if (!textarea || !countEl) return;

    const text = textarea.value.trim();
    const wordCount = text ? text.split(/\s+/).length : 0;
    countEl.textContent = `${wordCount} word${wordCount !== 1 ? 's' : ''}`;
}

function handleSave() {
    const textarea = document.querySelector('#journal-content');
    const content = textarea?.value.trim();

    if (!content) {
        showJournalToast('Please write something first');
        return;
    }

    const entry = {
        id: currentEntry?.id,
        verseRef: currentVerse.reference,
        verseText: currentVerse.text,
        content: content,
        createdAt: currentEntry?.createdAt
    };

    const savedEntry = saveJournalEntry(entry);

    // Log analytics
    const wordCount = content.split(/\s+/).length;
    if (currentEntry) {
        logJournalEdit(savedEntry.id, wordCount);
    } else {
        logJournalCreate(currentVerse.reference, wordCount);
    }

    showJournalToast(currentEntry ? 'Note updated' : 'Note saved');
    hideJournalEditor();
}

function handleDelete() {
    if (!currentEntry) return;

    if (confirm('Delete this note?')) {
        deleteJournalEntry(currentEntry.id);
        showJournalToast('Note deleted');
        hideJournalEditor();
    }
}

export function hideJournalEditor() {
    if (!currentOverlay) return;

    currentOverlay.classList.remove('active');
    setTimeout(() => {
        currentOverlay?.remove();
        currentOverlay = null;
        currentVerse = null;
        currentEntry = null;
    }, 300);
}

function showJournalToast(message) {
    const existing = document.querySelector('.journal-toast');
    if (existing) existing.remove();

    const toast = document.createElement('div');
    toast.className = 'journal-toast';
    toast.textContent = message;
    document.body.appendChild(toast);

    requestAnimationFrame(() => {
        toast.classList.add('active');
    });

    setTimeout(() => {
        toast.classList.remove('active');
        setTimeout(() => toast.remove(), 300);
    }, 2000);
}

export default { showJournalEditor, hideJournalEditor };
